/* ==========================================================================
   lp-landing.js
   Behaviour for the Moor landing-page section library (sections/lp-*).

   Vanilla, no libraries. Every component in here follows the same contract:

   - One instance per section element, tracked in a WeakMap. Re-initialising a
     section tears the previous instance down first, so the theme editor's
     shopify:section:load can never leave two sets of timers behind.
   - All listeners are registered through a single AbortController per instance
     and dropped with one abort() on teardown.
   - Anything that ticks (the carousel) is paused when the section is off-screen
     (IntersectionObserver) and when the tab is hidden (visibilitychange), and
     never starts at all under prefers-reduced-motion.
   - Scroll/touch listeners are passive.
   ========================================================================== */

(function () {
  'use strict';

  if (window.__lpLanding) return;
  window.__lpLanding = true;

  var instances = new WeakMap();
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------------------------------------------------------------------
     Express checkout — snippets/lp-buy-form.liquid

     Adds the line and sends the shopper straight to Shopify's checkout, where
     Tabby is one of the live payment methods.

     A native <button name="checkout"> submit cannot be used: the theme's cart
     component intercepts this form and the shopper never leaves the page
     (verified live). So the add is an explicit POST to the theme's own
     cart_add_url followed by a navigation — the drawer-staleness problem that
     ruled this approach out for Add to cart does not apply here, because the
     page is being left behind anyway.
     --------------------------------------------------------------------- */

  function initExpressCheckout(root, signal) {
    root.querySelectorAll('[data-lp-express-checkout]').forEach(function (button) {
      button.addEventListener(
        'click',
        function () {
          var form = button.closest('form');
          var addUrl = window.routes && window.routes.cart_add_url;
          if (!form || !addUrl) return;

          if (button.getAttribute('aria-busy') === 'true') return;
          button.setAttribute('aria-busy', 'true');

          var body = new FormData();
          var id = form.querySelector('[name="id"]');
          var qty = form.querySelector('[name="quantity"]');
          if (id) body.append('id', id.value);
          body.append('quantity', (qty && qty.value) || '1');

          fetch(addUrl, {
            method: 'POST',
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: body,
            // Tie the request to the instance so a section teardown mid-flight does not
            // leave a pending promise chain holding this button, its form and the whole
            // section subtree alive.
            signal: signal,
          })
            .then(function (response) {
              // Only leave the page if the line actually went in. Navigating on a
              // failed add drops the shopper on an empty checkout, which bounces
              // them to the home page — seen for real when the store returned 429.
              if (!response.ok) throw new Error('add failed');
              var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
              window.location.href = root + 'checkout';
            })
            .catch(function (error) {
              // An abort means the section was torn down, not that the add failed.
              // Falling back to .click() here would fire on a detached form.
              if (error && error.name === 'AbortError') return;
              button.removeAttribute('aria-busy');
              // Fall back to the ordinary Add to cart, which surfaces the theme's
              // own error handling rather than failing silently.
              var add = form.querySelector('[data-lp-add]');
              if (add) add.click();
            });
        },
        { signal: signal }
      );
    });
  }

  /* ---------------------------------------------------------------------
     Quantity tiers — sections/lp-offer-tiers.liquid

     Both landing-page products are single-variant, so a tier is a quantity,
     not a variant. Selecting a tier writes that quantity into every buy form
     on the page (hero, closing CTA, sticky bar) and fires input/change so the
     theme's own listeners recompute.
     --------------------------------------------------------------------- */

  function setQuantity(input, value) {
    var next = Math.max(1, parseInt(value, 10) || 1);
    if (String(input.value) === String(next)) return;
    input.value = next;
    // <xo-cart-quantity> keeps the quantity in the xo store from these events;
    // without them <xo-cart-add> would still submit the previous number.
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function applyTier(quantity) {
    document.querySelectorAll('.lp input[name="quantity"]').forEach(function (input) {
      setQuantity(input, quantity);
    });
    document.querySelectorAll('[data-lp-tier-input]').forEach(function (radio) {
      var checked = String(radio.value) === String(quantity);
      if (radio.checked !== checked) radio.checked = checked;
      var card = radio.closest('[data-lp-tier]');
      if (card) card.classList.toggle('is-selected', checked);
    });
  }

  function initTiers(root, signal) {
    var radios = root.querySelectorAll('[data-lp-tier-input]');
    if (!radios.length) return;

    radios.forEach(function (radio) {
      radio.addEventListener(
        'change',
        function () {
          if (radio.checked) applyTier(radio.value);
        },
        { signal: signal }
      );
    });

    // Whole card is clickable, but the radio stays the accessible control.
    root.querySelectorAll('[data-lp-tier]').forEach(function (card) {
      card.addEventListener(
        'click',
        function (event) {
          if (event.target.closest('label, input, a, button')) return;
          var radio = card.querySelector('[data-lp-tier-input]');
          if (!radio || radio.checked) return;
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        },
        { signal: signal }
      );
    });

    var preselected = root.querySelector('[data-lp-tier-input]:checked');
    if (preselected) applyTier(preselected.value);
  }

  /* ---------------------------------------------------------------------
     Swiper — the ONE carousel engine, shared by every lp- section that
     carousels (hero gallery, bundle-features cards, problems, testimonials).
     There is deliberately no second implementation.

     The track is a CSS scroll-snap row, so touch swiping and momentum are the
     browser's own — no touch handlers, no drag maths, and it keeps working if
     this script never runs. JS adds only: autoplay, arrows, dots and keyboard.

     WCAG 2.2.2 (pause, stop, hide) is satisfied by the pause rules below:
     autoplay stops on hover, on touch and on focus, whenever the section is
     off-screen or the tab is hidden, and never starts at all under
     prefers-reduced-motion.

     All position maths goes through getBoundingClientRect rather than
     offsetLeft/scrollLeft. Under RTL scrollLeft is negative in modern browsers
     and offsetLeft still measures from the physical left, so the arithmetic
     that works LTR silently jumps to the wrong slide in Arabic. Rect deltas fed
     to scrollBy are direction-agnostic.
     --------------------------------------------------------------------- */

  function initSwiper(carousel, signal, teardowns) {
    var track = carousel.querySelector('[data-lp-carousel-track]');
    var slides = track ? Array.prototype.slice.call(track.children) : [];
    if (!track || slides.length < 2) return;

    var dots = Array.prototype.slice.call(carousel.querySelectorAll('[data-lp-carousel-dot]'));
    var delay = parseInt(carousel.getAttribute('data-lp-autoplay'), 10) || 0;
    var timer = null;
    var frame = null;
    var visible = false;
    var index = 0;

    function goTo(next) {
      // Wraps in both directions, so the swiper loops infinitely.
      index = (next + slides.length) % slides.length;
      var delta =
        slides[index].getBoundingClientRect().left - track.getBoundingClientRect().left;
      // scrollIntoView would also scroll the page; move the track only.
      track.scrollBy({ left: delta, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    }

    function syncDots() {
      var trackRect = track.getBoundingClientRect();
      var mid = trackRect.left + trackRect.width / 2;
      var closest = 0;
      var best = Infinity;
      slides.forEach(function (slide, i) {
        var rect = slide.getBoundingClientRect();
        var distance = Math.abs(rect.left + rect.width / 2 - mid);
        if (distance < best) {
          best = distance;
          closest = i;
        }
      });
      index = closest;
      dots.forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === closest);
        if (i === closest) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    // The scroll listener is passive and must stay cheap; the rect reads are
    // coalesced into one frame instead of running per scroll event.
    function queueSync() {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(function () {
        frame = null;
        syncDots();
      });
    }

    function stop() {
      if (timer === null) return;
      window.clearInterval(timer);
      timer = null;
    }

    function start() {
      stop();
      if (!delay || reduceMotion.matches) return;
      if (!visible || document.hidden) return;
      timer = window.setInterval(function () {
        goTo(index + 1);
      }, delay);
    }

    track.addEventListener('scroll', queueSync, { passive: true, signal: signal });

    carousel.addEventListener('pointerenter', stop, { signal: signal });
    carousel.addEventListener('pointerleave', start, { signal: signal });
    carousel.addEventListener('focusin', stop, { signal: signal });
    carousel.addEventListener('focusout', start, { signal: signal });
    // A swipe should not fight the autoplay timer.
    track.addEventListener('pointerdown', stop, { passive: true, signal: signal });
    track.addEventListener('pointerup', start, { passive: true, signal: signal });

    // Keyboard: the track is focusable, so left/right step a whole slide rather
    // than the browser's default few-pixel nudge. Physical arrow keys keep their
    // physical meaning under RTL, which is what keyboard users expect.
    track.addEventListener(
      'keydown',
      function (event) {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        var rtl = window.getComputedStyle(track).direction === 'rtl';
        var forward = rtl ? event.key === 'ArrowLeft' : event.key === 'ArrowRight';
        event.preventDefault();
        stop();
        goTo(forward ? index + 1 : index - 1);
      },
      { signal: signal }
    );

    carousel.querySelectorAll('[data-lp-carousel-prev]').forEach(function (button) {
      button.addEventListener('click', function () { goTo(index - 1); }, { signal: signal });
    });
    carousel.querySelectorAll('[data-lp-carousel-next]').forEach(function (button) {
      button.addEventListener('click', function () { goTo(index + 1); }, { signal: signal });
    });
    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () { goTo(i); }, { signal: signal });
    });

    document.addEventListener(
      'visibilitychange',
      function () { if (document.hidden) stop(); else start(); },
      { signal: signal }
    );

    var observer = new IntersectionObserver(
      function (entries) {
        visible = entries[0].isIntersecting;
        if (visible) start();
        else stop();
      },
      { threshold: 0.2 }
    );
    observer.observe(carousel);

    if (reduceMotion.addEventListener) {
      reduceMotion.addEventListener('change', function () { start(); }, { signal: signal });
    }

    syncDots();

    teardowns.push(function () {
      stop();
      if (frame !== null) window.cancelAnimationFrame(frame);
      observer.disconnect();
    });
  }

  function initCarousel(root, signal, teardowns) {
    // A section may hold more than one swiper.
    root.querySelectorAll('[data-lp-carousel]').forEach(function (carousel) {
      initSwiper(carousel, signal, teardowns);
    });
  }

  /* ---------------------------------------------------------------------
     Mobile sticky purchase bar — sections/lp-sticky-bar.liquid

     Revealed once the hero's buy area has scrolled out of view. While it is
     visible the document gets a class that adds matching bottom padding, so
     the bar can never sit on top of the footer.
     --------------------------------------------------------------------- */

  function initStickyBar(root, signal, teardowns) {
    var bar = root.querySelector('[data-lp-sticky]');
    if (!bar) return;

    var anchor = document.querySelector('[data-lp-sticky-anchor]');
    // The closing CTA. Once it is on screen the page already shows a full-width
    // buy button, so the bar stands down rather than competing with it.
    var end = document.querySelector('[data-lp-sticky-end]');
    var observers = [];
    var pastAnchor = false;
    var atEnd = false;

    function apply() {
      var state = pastAnchor && !atEnd;
      bar.classList.toggle('is-visible', state);
      bar.setAttribute('aria-hidden', state ? 'false' : 'true');
      document.documentElement.classList.toggle('lp-has-sticky-bar', state);
    }

    function observe(target, onEntry) {
      var observer = new IntersectionObserver(function (entries) {
        onEntry(entries[0]);
        apply();
      }, { threshold: 0 });
      observer.observe(target);
      observers.push(observer);
    }

    if (anchor) {
      // Show once the anchor has left the viewport upwards.
      observe(anchor, function (entry) {
        pastAnchor = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      });
    } else {
      var onScroll = function () {
        pastAnchor = window.scrollY > 600;
        apply();
      };
      window.addEventListener('scroll', onScroll, { passive: true, signal: signal });
      onScroll();
    }

    if (end) {
      observe(end, function (entry) {
        atEnd = entry.isIntersecting;
      });
    }

    teardowns.push(function () {
      observers.forEach(function (observer) { observer.disconnect(); });
      pastAnchor = false;
      atEnd = false;
      apply();
    });
  }

  /* ---------------------------------------------------------------------
     Wave dividers — snippets/lp-wave.liquid

     Revision round 1, §10. CSS drives the drift entirely (a single transform
     keyframe); this only pauses it once the wave has scrolled off-screen, so
     it is never animating in a tab the visitor isn't looking at.
     prefers-reduced-motion is handled in CSS, not here — see the global
     animation-duration override in assets/lp-landing.css.
     --------------------------------------------------------------------- */

  function initWaves(root, signal, teardowns) {
    var waves = root.querySelectorAll('[data-lp-wave]');
    if (!waves.length) return;

    /* A wave animates only when it is BOTH on screen and in a visible tab.
       Previously only the on-screen half was implemented, so a backgrounded tab
       kept every visible wave animating. Intersection state is tracked so that
       returning to the tab restores exactly the waves that are actually in view -
       an IntersectionObserver will not re-fire on visibilitychange by itself. */
    var onScreen = new Set();

    function apply() {
      var hidden = document.hidden;
      waves.forEach(function (wave) {
        wave.classList.toggle('is-paused', hidden || !onScreen.has(wave));
      });
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) onScreen.add(entry.target);
          else onScreen.delete(entry.target);
        });
        apply();
      },
      { threshold: 0 }
    );
    waves.forEach(function (wave) { observer.observe(wave); });

    document.addEventListener('visibilitychange', apply, { signal: signal });

    teardowns.push(function () {
      observer.disconnect();
      onScreen.clear();
    });
  }

  /* ---------------------------------------------------------------------
     Back to top — sections/lp-sticky-bar.liquid

     Revision round 1, §9. The theme ships its own scroll-top button
     (snippets/scroll-top.liquid, rendered site-wide from layout/theme.liquid),
     but it is a 3rem/4rem tap target in the theme's black/white palette with no
     accessible name — short of the brief's 44px-minimum, cyan, labelled button.
     It is an existing theme file, so it cannot be edited; this renders a
     second, compliant button and CSS hides the original one only on pages that
     carry an lp- section (see the :has() rule in lp-landing.css), so the two
     are never both on screen.
     --------------------------------------------------------------------- */

  function initBackToTop(root, signal, teardowns) {
    var button = root.querySelector('[data-lp-back-to-top]');
    if (!button) return;

    var threshold = window.innerHeight * 1.5;

    function onScroll() {
      button.classList.toggle('is-visible', window.scrollY > threshold);
    }

    window.addEventListener('scroll', onScroll, { passive: true, signal: signal });
    onScroll();

    button.addEventListener(
      'click',
      function () {
        window.scrollTo({ top: 0, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      },
      { signal: signal }
    );

    teardowns.push(function () {
      button.classList.remove('is-visible');
    });
  }

  /* ---------------------------------------------------------------------
     Instance lifecycle
     --------------------------------------------------------------------- */

  function destroy(root) {
    var instance = instances.get(root);
    if (!instance) return;
    instance.controller.abort();
    instance.teardowns.forEach(function (fn) {
      try { fn(); } catch (error) { /* teardown must never throw */ }
    });
    instances.delete(root);
  }

  function init(root) {
    if (!root || !root.querySelector) return;
    destroy(root);

    var controller = new AbortController();
    var teardowns = [];
    instances.set(root, { controller: controller, teardowns: teardowns });

    initExpressCheckout(root, controller.signal);
    initTiers(root, controller.signal);
    initCarousel(root, controller.signal, teardowns);
    initStickyBar(root, controller.signal, teardowns);
    initBackToTop(root, controller.signal, teardowns);
    initWaves(root, controller.signal, teardowns);
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-lp-section]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); }, { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('[data-lp-section]');
    if (root) init(root);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var root = event.target.querySelector('[data-lp-section]');
    if (root) destroy(root);
  });
})();
